/**
 * Search that understands how Arabic — and, since phase 40, Hebrew — is typed.
 *
 * «معمول», «مَعمول» and «معمـول» are one word; so are «أصابع» and «اصابع»,
 * «كنافة» and «كنافه». The catalogue's names are matched after both sides
 * are folded the same way: no diacritics or tatweel, one alef, ى → ي,
 * ة → ه, hamza seats → their letter, Latin lower-cased.
 *
 * The character classes are built from code points rather than written out:
 * a literal combining mark in source is invisible and easy to break.
 */

const ch = (code: number) => String.fromCharCode(code)

// U+064B–U+065F harakat, U+0670 superscript alef, U+0640 tatweel.
const DIACRITICS = new RegExp(`[${ch(0x064b)}-${ch(0x065f)}${ch(0x0670)}${ch(0x0640)}]`, 'g')
// أ إ آ ٱ → ا
const ALEFS = new RegExp(`[${ch(0x0623)}${ch(0x0625)}${ch(0x0622)}${ch(0x0671)}]`, 'g')
const ALEF = ch(0x0627)
const ALEF_MAKSURA = new RegExp(ch(0x0649), 'g') // ى
const YA = ch(0x064a) // ي
const TA_MARBUTA = new RegExp(ch(0x0629), 'g') // ة
const HA = ch(0x0647) // ه
const WAW_HAMZA = new RegExp(ch(0x0624), 'g') // ؤ
const WAW = ch(0x0648)
const YA_HAMZA = new RegExp(ch(0x0626), 'g') // ئ

// Phase 40 — Hebrew too: U+0591–U+05C7 are its cantillation and vowel
// points, and a word's last letter has its own form (ך ם ן ף ץ) that a
// customer typing the middle of a word would not use.
const HEBREW_POINTS = new RegExp(`[${ch(0x0591)}-${ch(0x05c7)}]`, 'g')
const HEBREW_FINALS: Record<string, string> = {
  [ch(0x05da)]: ch(0x05db), // ך → כ
  [ch(0x05dd)]: ch(0x05de), // ם → מ
  [ch(0x05df)]: ch(0x05e0), // ן → נ
  [ch(0x05e3)]: ch(0x05e4), // ף → פ
  [ch(0x05e5)]: ch(0x05e6), // ץ → צ
}
const HEBREW_FINAL = new RegExp(`[${Object.keys(HEBREW_FINALS).join('')}]`, 'g')

export function foldArabic(text: string): string {
  return text
    .normalize('NFKC')
    .replace(DIACRITICS, '')
    .replace(ALEFS, ALEF)
    .replace(ALEF_MAKSURA, YA)
    .replace(TA_MARBUTA, HA)
    .replace(WAW_HAMZA, WAW)
    .replace(YA_HAMZA, YA)
    .replace(HEBREW_POINTS, '')
    .replace(HEBREW_FINAL, (letter) => HEBREW_FINALS[letter] ?? letter)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Whether every word of the query appears in the text, in any order. A
 * product named «بقلاوة بالفستق الحلبي» answers «فستق بقلاوه».
 */
export function matches(text: string, query: string): boolean {
  const haystack = foldArabic(text)
  const words = foldArabic(query).split(' ').filter(Boolean)
  return words.length > 0 && words.every((word) => haystack.includes(word))
}
